import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, TextArea } from '@/lib';
import { EmailCampaignFormInput } from '../EmailCampaignValidation';

type TitleDescriptionStepProps = {
  onNext: () => Promise<boolean>;
  onCancel?: () => void;
};

export const TitleDescriptionStep: FC<TitleDescriptionStepProps> = ({ onNext, onCancel }) => {
  const { t } = useTranslation('campaign');
  const { t: tCommon } = useTranslation('COMMON');
  const {
    register,
    watch,
    trigger,
    formState: { errors, dirtyFields, touchedFields },
  } = useFormContext<EmailCampaignFormInput>();

  const campaignName = watch('name');
  const campaignDescription = watch('description');

  useEffect(() => {
    if (dirtyFields.name || touchedFields.name) {
      void trigger('name');
    }
  }, [campaignName, dirtyFields.name, touchedFields.name, trigger]);

  useEffect(() => {
    if (dirtyFields.description) {
      void trigger('description');
    }
  }, [campaignDescription, dirtyFields.description, trigger]);

  const handleNext = async () => {
    await onNext();
  };

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('emailWorkspace.campaigns.createCampaign')}</h2>
        <p className='mt-2 text-sm text-gray-600'>{t('emailWorkspace.campaigns.heroSubtitle')}</p>
      </div>

      <div className='grid gap-6'>
        <div>
          <Label htmlFor='name' isRequired>
            {t('enterCampaignTitleLabel')}
          </Label>
          <Input {...register('name')} placeholder={t('enterCampaignTitlePlaceholder')} errorMsg={errors.name?.message} />
        </div>

        <div>
          <Label htmlFor='description'>{t('enterCampaignDescriptionLabel')}</Label>
          <TextArea
            className='max-h-36'
            {...register('description')}
            placeholder={t('enterCampaignDescriptionPlaceholder')}
            errorMsg={errors.description?.message}
          />
        </div>
      </div>

      <div className='flex flex-col justify-end gap-3 sm:flex-row'>
        {onCancel ? <Button intent='secondary' className='w-full max-w-[220px]' text={tCommon('cancel')} onClick={onCancel} /> : null}
        <Button className='w-full max-w-[220px]' text={t('next')} onClick={handleNext} />
      </div>
    </div>
  );
};
